import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { Box, Typography } from '@mui/material';
import Map, { Layer, MapRef, Marker, Source } from 'react-map-gl/mapbox';
import mapboxgl from 'mapbox-gl';
import type { LineLayer } from 'mapbox-gl';
import type { Feature, LineString } from 'geojson';
import { useTranslation } from 'react-i18next';
import { MapBackLocationTypeIcon } from './MapBackLocationIcon';
import { MapUserIcon } from '~/assets/icons';
import iconMapPlayback from '~/assets/images/iconmapplayback.png';
import { JourneyPoint, TimelineItemUI } from '~/utils/interface/mapback';
import type { IPersonStatus } from '~/utils/interface/map';
import { getJourneySegmentMarkerColors } from '../constants/segmentTypeColors';
import { useJourneyPersonStatuses } from '../context/JourneyPersonStatusContext';
import { useHoveredSegmentIndex } from '../context/JourneyHighlightContext';
import {
  JOURNEY_FLY_TO_DURATION_MS,
  JOURNEY_FLY_TO_ZOOM,
  JOURNEY_INITIAL_VIEW_STATE,
  JOURNEY_MAP_STYLE,
  JOURNEY_SIDEBAR_PIN_MARKER,
  PLAYBACK_INTERVAL_BY_SPEED,
  PlaybackSpeed,
} from '../constants/journey';
import JourneyHoverInfoCard from './JourneyHoverInfoCard';
import styles from '../Journey.module.scss';

interface JourneyMapProps {
  allPoints: JourneyPoint[];
  timelineItems: TimelineItemUI[];
  playbackProgressIndex: number;
  isPlaying: boolean;
  currentPointIndex: number;
  playSpeed: PlaybackSpeed;
  onPlaybackProgressChange?: (progressIndex: number) => void;
  onSegmentClick: (index: number) => void;
  selectedSegmentIndex?: number | null;
  flyToTarget?: {
    latitude: number;
    longitude: number;
    key: string;
    zoom?: number;
    adaptiveZoomThreshold?: number;
    adaptiveZoomMin?: number;
    resumePlaybackAfter?: boolean;
  } | null;
  onFlyToTargetComplete?: (target: NonNullable<JourneyMapProps['flyToTarget']>) => void;
  pinnedInfoCard?: {
    point: JourneyPoint;
    startAddress?: string;
    key: string;
  } | null;
  sidebarSelectionMarker?: {
    latitude: number;
    longitude: number;
    key: string;
  } | null;
  selectedPersonLocation?: {
    latitude: number | null;
    longitude: number | null;
  } | null;
  fullName?: string;
  isLoading?: boolean;
}

interface HoveredInfo {
  point: JourneyPoint;
  startAddress?: string;
}

const JOURNEY_LINE_REMAINING_SOLID = '#d8d5f4';
const TRAVELED_SOURCE_ID = 'journey-line-traveled';
const REMAINING_SOURCE_ID = 'journey-line-remaining';
const PARENT_PROGRESS_NOTIFY_MS = 100;

const traveledLineLayer: LineLayer = {
  id: 'journey-line-traveled-layer',
  type: 'line',
  source: TRAVELED_SOURCE_ID,
  paint: {
    'line-color': '#2E89C8',
    'line-width': 4,
    'line-opacity': 1,
  },
};

const remainingLineLayer: LineLayer = {
  id: 'journey-line-remaining-layer',
  type: 'line',
  source: REMAINING_SOURCE_ID,
  paint: {
    'line-color': JOURNEY_LINE_REMAINING_SOLID,
    'line-width': 4,
    'line-opacity': 1,
  },
};

const highlightedSegmentLayer: LineLayer = {
  id: 'journey-line-highlighted-segment',
  type: 'line',
  source: 'journey-line-highlighted-segment',
  paint: {
    'line-color': '#F59E0B',
    'line-width': 4,
    'line-opacity': 1,
  },
};

interface PlaybackMarkerContentProps {
  segmentType: JourneyPoint['segmentType'];
  showAlertWave: boolean;
  personStatuses: IPersonStatus[];
}

const PlaybackMarkerContent: React.FC<PlaybackMarkerContentProps> = ({
  segmentType,
  showAlertWave,
  personStatuses,
}) => {
  const markerColors = getJourneySegmentMarkerColors(segmentType, personStatuses);

  return (
    <Box className='relative flex h-7 w-7 items-center justify-center'>
      {showAlertWave && (
        <>
          <Box
            component='span'
            className='absolute inset-[-4px] rounded-full border-2 animate-ping'
            style={{ borderColor: `${markerColors.bg}73` }}
          />
          <Box
            component='span'
            className='absolute inset-[-8px] rounded-full border animate-pulse'
            style={{ borderColor: `${markerColors.bg}59` }}
          />
        </>
      )}
      <Box
        className='relative z-10 flex h-7 w-7 items-center justify-center rounded-full border shadow-[0_2px_8px_rgba(0,0,0,0.18)]'
        style={{ borderColor: markerColors.border, backgroundColor: markerColors.bg }}
      >
        <MapUserIcon className='h-3.5 w-3.5 [filter:brightness(0)_invert(1)]' />
      </Box>
    </Box>
  );
};

// ── helpers ─────────────────────────────────────────────────────────────────

function clampProgressIndex(progressIndex: number, pointCount: number): number {
  const maxIndex = Math.max(0, pointCount - 1);
  return Math.min(Math.max(progressIndex, 0), maxIndex);
}

function getInterpolatedPosition(
  points: JourneyPoint[],
  progressIndex: number
): { longitude: number; latitude: number } | null {
  if (points.length === 0) return null;

  const clamped = clampProgressIndex(progressIndex, points.length);
  const floorIndex = Math.floor(clamped);
  const ceilIndex = Math.min(Math.ceil(clamped), points.length - 1);

  if (floorIndex === ceilIndex) {
    const point = points[floorIndex];
    return { longitude: point.longitude, latitude: point.latitude };
  }

  const ratio = clamped - floorIndex;
  const from = points[floorIndex];
  const to = points[ceilIndex];
  return {
    longitude: from.longitude + ratio * (to.longitude - from.longitude),
    latitude: from.latitude + ratio * (to.latitude - from.latitude),
  };
}

function buildTraveledLineGeoJSON(points: JourneyPoint[], progressIndex: number): Feature<LineString> {
  if (points.length === 0) {
    return { type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: [] } };
  }

  const clamped = clampProgressIndex(progressIndex, points.length);
  const floorIndex = Math.floor(clamped);
  const coordinates: [number, number][] = points
    .slice(0, floorIndex + 1)
    .map((point) => [point.longitude, point.latitude]);

  const interpolated = getInterpolatedPosition(points, clamped);
  if (!interpolated) {
    return { type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates } };
  }

  const tip: [number, number] = [interpolated.longitude, interpolated.latitude];
  const last = coordinates[coordinates.length - 1];
  if (!last || last[0] !== tip[0] || last[1] !== tip[1]) {
    coordinates.push(tip);
  }

  return {
    type: 'Feature',
    properties: {},
    geometry: { type: 'LineString', coordinates },
  };
}

function resolveMarkerLngLat(
  points: JourneyPoint[],
  progressIndex: number,
  map: mapboxgl.Map,
  fallback: { longitude: number; latitude: number } | null
): [number, number] {
  const position = getInterpolatedPosition(points, progressIndex);
  if (position) return [position.longitude, position.latitude];
  if (fallback) return [fallback.longitude, fallback.latitude];

  const center = map.getCenter();
  return [center.lng, center.lat];
}

function syncRoutePlayback(
  map: mapboxgl.Map | undefined,
  marker: mapboxgl.Marker | null,
  points: JourneyPoint[],
  progressIndex: number,
  lastFloorIndexRef: React.MutableRefObject<number>,
  onMarkerStyleChange?: (floorIndex: number) => void
): void {
  if (!map || points.length < 2) return;

  const traveledSource = map.getSource(TRAVELED_SOURCE_ID) as mapboxgl.GeoJSONSource | undefined;
  traveledSource?.setData(buildTraveledLineGeoJSON(points, progressIndex));

  const position = getInterpolatedPosition(points, progressIndex);
  if (position) {
    marker?.setLngLat([position.longitude, position.latitude]);
  }

  const floorIndex = Math.floor(clampProgressIndex(progressIndex, points.length));
  if (floorIndex !== lastFloorIndexRef.current) {
    lastFloorIndexRef.current = floorIndex;
    onMarkerStyleChange?.(floorIndex);
  }
}

function resolveFlyToZoom(
  map: mapboxgl.Map,
  flyToTarget: NonNullable<JourneyMapProps['flyToTarget']>
): number {
  if (flyToTarget.adaptiveZoomThreshold === undefined) {
    return flyToTarget.zoom ?? JOURNEY_FLY_TO_ZOOM;
  }

  const currentZoom = map.getZoom();
  const maxZoom = flyToTarget.adaptiveZoomThreshold;
  const minZoom = flyToTarget.adaptiveZoomMin;

  if (currentZoom > maxZoom) return flyToTarget.zoom ?? maxZoom;
  if (minZoom !== undefined && currentZoom < minZoom) return minZoom;
  return currentZoom;
}

// ── component ───────────────────────────────────────────────────────────────

const JourneyMap: React.FC<JourneyMapProps> = ({
  allPoints,
  timelineItems,
  playbackProgressIndex,
  isPlaying,
  currentPointIndex,
  playSpeed,
  onPlaybackProgressChange,
  onSegmentClick,
  selectedSegmentIndex = null,
  flyToTarget = null,
  onFlyToTargetComplete,
  pinnedInfoCard = null,
  sidebarSelectionMarker = null,
  selectedPersonLocation = null,
  fullName = '',
  isLoading = false,
}) => {
  const { t } = useTranslation('admin/journey');
  const personStatuses = useJourneyPersonStatuses();
  const hoveredSegmentIndex = useHoveredSegmentIndex();
  const highlightedSegmentIndex = hoveredSegmentIndex ?? selectedSegmentIndex;
  const mapRef = useRef<MapRef | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const playbackFrameRef = useRef<number | null>(null);
  const playbackFloorRef = useRef(-1);
  const liveProgressRef = useRef(0);
  const playbackMarkerRef = useRef<mapboxgl.Marker | null>(null);
  const playbackMarkerRootRef = useRef<Root | null>(null);
  const playedTraveledLineRef = useRef<Feature<LineString> | null>(null);
  const safeAllPoints = useMemo(() => (Array.isArray(allPoints) ? allPoints : []), [allPoints]);
  const safeTimelineItems = useMemo(() => (Array.isArray(timelineItems) ? timelineItems : []), [timelineItems]);
  const idleProgressIndex = clampProgressIndex(playbackProgressIndex, safeAllPoints.length);

  const clearPlaybackFrame = useCallback(() => {
    if (playbackFrameRef.current === null) return;
    cancelAnimationFrame(playbackFrameRef.current);
    playbackFrameRef.current = null;
  }, []);

  const updatePlaybackMarkerAppearance = useCallback(
    (floorIndex: number) => {
      const point = safeAllPoints[floorIndex];
      if (!point || !playbackMarkerRootRef.current) return;

      playbackMarkerRootRef.current.render(
        <PlaybackMarkerContent
          segmentType={point.segmentType}
          showAlertWave={point.segmentType !== 'route'}
          personStatuses={personStatuses}
        />
      );
    },
    [safeAllPoints, personStatuses]
  );

  const disposePlaybackMarker = useCallback(() => {
    playbackMarkerRef.current?.remove();
    playbackMarkerRef.current = null;
    playbackMarkerRootRef.current?.unmount();
    playbackMarkerRootRef.current = null;
  }, []);

  const ensurePlaybackMarker = useCallback(
    (map: mapboxgl.Map, lngLat: [number, number]) => {
      const existing = playbackMarkerRef.current;
      if (existing) {
        const markerElement = existing.getElement();
        const mapContainer = map.getContainer();
        const isAttachedToMap = Boolean(markerElement && mapContainer.contains(markerElement));

        if (isAttachedToMap) {
          existing.setLngLat(lngLat);
          markerElement.style.display = '';
          return;
        }

        disposePlaybackMarker();
      }

      const element = document.createElement('div');
      playbackMarkerRootRef.current = createRoot(element);
      playbackMarkerRootRef.current.render(
        <PlaybackMarkerContent segmentType='route' showAlertWave={false} personStatuses={personStatuses} />
      );

      playbackMarkerRef.current = new mapboxgl.Marker({ element, anchor: 'center' });
      playbackMarkerRef.current.setLngLat(lngLat).addTo(map);
    },
    [disposePlaybackMarker, personStatuses]
  );

  const setPlaybackMarkerVisible = useCallback((visible: boolean) => {
    const element = playbackMarkerRef.current?.getElement();
    if (!element) return;
    element.style.display = visible ? '' : 'none';
  }, []);

  const safeCurrentPointIndex = Math.floor(idleProgressIndex);
  const [hoveredInfo, setHoveredInfo] = useState<HoveredInfo | null>(null);
  const activeInfoCard = hoveredInfo ?? (pinnedInfoCard ? { point: pinnedInfoCard.point, startAddress: pinnedInfoCard.startAddress } : null);

  const playbackPoint = safeAllPoints[safeCurrentPointIndex] ?? null;
  const fallbackLocation = useMemo(() => {
    if (
      selectedPersonLocation &&
      Number.isFinite(selectedPersonLocation.latitude) &&
      Number.isFinite(selectedPersonLocation.longitude)
    ) {
      return {
        latitude: selectedPersonLocation.latitude as number,
        longitude: selectedPersonLocation.longitude as number,
      };
    }
    return null;
  }, [selectedPersonLocation]);

  const fullRouteData = useMemo<Feature<LineString>>(
    () => ({
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'LineString',
        coordinates: safeAllPoints.map((point) => [point.longitude, point.latitude]),
      },
    }),
    [safeAllPoints]
  );

  const traveledLineData = useMemo(
    () => buildTraveledLineGeoJSON(safeAllPoints, idleProgressIndex),
    [safeAllPoints, idleProgressIndex]
  );

  if (!isPlaying || !playedTraveledLineRef.current) {
    playedTraveledLineRef.current = traveledLineData;
  }

  const activeTraveledLineData = isPlaying ? playedTraveledLineRef.current : traveledLineData;

  useEffect(() => {
    return () => {
      clearPlaybackFrame();
      disposePlaybackMarker();
    };
  }, [clearPlaybackFrame, disposePlaybackMarker]);

  useEffect(() => {
    if (!isLoading) return;
    disposePlaybackMarker();
  }, [isLoading, disposePlaybackMarker]);

  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map || safeAllPoints.length === 0) return;

    const markerLngLat = resolveMarkerLngLat(safeAllPoints, idleProgressIndex, map, fallbackLocation);
    ensurePlaybackMarker(map, markerLngLat);
    updatePlaybackMarkerAppearance(Math.floor(idleProgressIndex));

    if (!isPlaying) {
      playbackFloorRef.current = -1;
      syncRoutePlayback(
        map,
        playbackMarkerRef.current,
        safeAllPoints,
        idleProgressIndex,
        playbackFloorRef,
        updatePlaybackMarkerAppearance
      );
    }
  }, [
    isPlaying,
    idleProgressIndex,
    safeAllPoints,
    fallbackLocation,
    ensurePlaybackMarker,
    updatePlaybackMarkerAppearance,
  ]);

  useEffect(() => {
    if (!isPlaying) return;

    const map = mapRef.current?.getMap();
    if (!map) return;

    const onRender = () => {
      syncRoutePlayback(
        map,
        playbackMarkerRef.current,
        safeAllPoints,
        liveProgressRef.current,
        playbackFloorRef,
        updatePlaybackMarkerAppearance
      );
    };

    map.on('render', onRender);
    return () => {
      map.off('render', onRender);
    };
  }, [isPlaying, safeAllPoints, updatePlaybackMarkerAppearance]);

  useEffect(() => {
    clearPlaybackFrame();
    if (!isPlaying || safeAllPoints.length === 0) return clearPlaybackFrame;

    const map = mapRef.current?.getMap();
    if (!map) return clearPlaybackFrame;

    const startIndex = clampProgressIndex(currentPointIndex, safeAllPoints.length);
    const nextIndex = startIndex + 1 >= safeAllPoints.length ? 0 : startIndex + 1;
    const waitMs = PLAYBACK_INTERVAL_BY_SPEED[playSpeed];
    const startedAt = performance.now();
    let lastParentNotifyAt = 0;
    const markerLngLat = resolveMarkerLngLat(safeAllPoints, startIndex, map, fallbackLocation);

    playbackFloorRef.current = -1;
    liveProgressRef.current = startIndex;
    playedTraveledLineRef.current = buildTraveledLineGeoJSON(safeAllPoints, startIndex);
    ensurePlaybackMarker(map, markerLngLat);
    updatePlaybackMarkerAppearance(Math.floor(startIndex));
    setPlaybackMarkerVisible(true);
    syncRoutePlayback(
      map,
      playbackMarkerRef.current,
      safeAllPoints,
      startIndex,
      playbackFloorRef,
      updatePlaybackMarkerAppearance
    );
    map.triggerRepaint();

    const tick = (now: number) => {
      const elapsed = now - startedAt;
      const ratio = waitMs > 0 ? Math.min(1, Math.max(0, elapsed / waitMs)) : 1;
      const progress = startIndex + ratio * (nextIndex - startIndex);

      liveProgressRef.current = progress;
      mapRef.current?.getMap().triggerRepaint();

      if (now - lastParentNotifyAt >= PARENT_PROGRESS_NOTIFY_MS || ratio >= 1) {
        onPlaybackProgressChange?.(progress);
        lastParentNotifyAt = now;
      }

      if (ratio < 1) {
        playbackFrameRef.current = requestAnimationFrame(tick);
      }
    };

    playbackFrameRef.current = requestAnimationFrame(tick);
    return clearPlaybackFrame;
  }, [
    isPlaying,
    currentPointIndex,
    playSpeed,
    safeAllPoints,
    fallbackLocation,
    clearPlaybackFrame,
    ensurePlaybackMarker,
    updatePlaybackMarkerAppearance,
    setPlaybackMarkerVisible,
    onPlaybackProgressChange,
  ]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const resizeMap = () => {
      mapRef.current?.getMap().resize();
    };

    resizeMap();

    const observer = new ResizeObserver(() => {
      resizeMap();
    });
    observer.observe(container);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!mapRef.current || safeAllPoints.length === 0) return;
    const map = mapRef.current.getMap();

    let minLng = Infinity,
      maxLng = -Infinity,
      minLat = Infinity,
      maxLat = -Infinity;
    for (const p of safeAllPoints) {
      if (p.longitude < minLng) minLng = p.longitude;
      if (p.longitude > maxLng) maxLng = p.longitude;
      if (p.latitude < minLat) minLat = p.latitude;
      if (p.latitude > maxLat) maxLat = p.latitude;
    }

    map.fitBounds(
      [
        [minLng, minLat],
        [maxLng, maxLat],
      ],
      { padding: 50, duration: 800, maxZoom: 16 }
    );
  }, [safeAllPoints]);

  useEffect(() => {
    if (!mapRef.current || safeAllPoints.length > 0 || !fallbackLocation) return;
    mapRef.current.getMap().flyTo({
      center: [fallbackLocation.longitude, fallbackLocation.latitude],
      zoom: 15,
      duration: 800,
    });
  }, [fallbackLocation, safeAllPoints.length]);

  useEffect(() => {
    if (!mapRef.current || !flyToTarget) return;
    if (!Number.isFinite(flyToTarget.latitude) || !Number.isFinite(flyToTarget.longitude)) return;

    const map = mapRef.current.getMap();
    let completed = false;

    const completeFlyTo = () => {
      if (completed) return;
      completed = true;
      if (flyToTarget.resumePlaybackAfter) {
        onFlyToTargetComplete?.(flyToTarget);
      }
    };

    const handleIdle = () => {
      completeFlyTo();
    };

    map.once('idle', handleIdle);
    const fallbackTimer = window.setTimeout(completeFlyTo, JOURNEY_FLY_TO_DURATION_MS + 100);

    map.flyTo({
      center: [flyToTarget.longitude, flyToTarget.latitude],
      zoom: resolveFlyToZoom(map, flyToTarget),
      duration: JOURNEY_FLY_TO_DURATION_MS,
    });

    window.requestAnimationFrame(() => {
      if (!map.isMoving() && !map.isZooming() && !map.isRotating()) {
        completeFlyTo();
      }
    });

    return () => {
      map.off('idle', handleIdle);
      window.clearTimeout(fallbackTimer);
    };
  }, [flyToTarget, onFlyToTargetComplete]);

  const highlightedSegmentData = useMemo(() => {
    if (highlightedSegmentIndex === null || highlightedSegmentIndex < 0) return null;

    const segmentPoints = safeAllPoints.filter((point) => point.segmentIndex === highlightedSegmentIndex);
    if (segmentPoints.length >= 2) {
      return {
        type: 'Feature' as const,
        properties: {},
        geometry: {
          type: 'LineString' as const,
          coordinates: segmentPoints.map((p) => [p.longitude, p.latitude]),
        },
      };
    }

    const from = safeTimelineItems[highlightedSegmentIndex]?.points?.[0];
    const to = safeTimelineItems[highlightedSegmentIndex + 1]?.points?.[0];
    if (!from || !to) return null;

    return {
      type: 'Feature' as const,
      properties: {},
      geometry: {
        type: 'LineString' as const,
        coordinates: [
          [from.longitude, from.latitude],
          [to.longitude, to.latitude],
        ],
      },
    };
  }, [highlightedSegmentIndex, safeAllPoints, safeTimelineItems]);

  if (isLoading) {
    return (
      <Box className='flex h-full w-full items-center justify-center rounded-lg border border-gray-200 bg-gray-50'>
        <Box className='flex flex-col items-center gap-2 text-gray-400'>
          <Box className='h-8 w-8 animate-spin rounded-full border-4 border-blue-300 border-t-blue-600' />
          <Typography component='span' className='text-sm'>
            {t('empty.loadingMap')}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box ref={containerRef} className='relative h-full w-full overflow-hidden rounded-lg border border-gray-200'>
      <JourneyHoverInfoCard
        point={activeInfoCard?.point ?? null}
        fullName={fullName}
        startAddress={activeInfoCard?.startAddress}
      />

      <Map
        ref={mapRef}
        mapboxAccessToken={process.env.REACT_APP_MAP_BOX_ACCESS_TOKEN}
        initialViewState={JOURNEY_INITIAL_VIEW_STATE}
        mapStyle={JOURNEY_MAP_STYLE}
        style={{ width: '100%', height: '100%' }}
        onLoad={() => {
          const map = mapRef.current?.getMap();
          map?.resize();
          if (!map || safeAllPoints.length === 0) return;

          const markerLngLat = resolveMarkerLngLat(safeAllPoints, idleProgressIndex, map, fallbackLocation);
          ensurePlaybackMarker(map, markerLngLat);
          updatePlaybackMarkerAppearance(Math.floor(idleProgressIndex));
        }}
      >
        {safeAllPoints.length >= 2 && (
          <>
            <Source id={REMAINING_SOURCE_ID} type='geojson' data={fullRouteData}>
              <Layer {...remainingLineLayer} />
            </Source>
            <Source id={TRAVELED_SOURCE_ID} type='geojson' data={activeTraveledLineData}>
              <Layer {...traveledLineLayer} />
            </Source>
          </>
        )}

        {safeAllPoints.length >= 2 && highlightedSegmentData && (
          <Source id='journey-line-highlighted-segment' type='geojson' data={highlightedSegmentData}>
            <Layer {...highlightedSegmentLayer} />
          </Source>
        )}

        {safeTimelineItems.map((item, idx) => {
          const firstPoint = item.points[0];
          if (!firstPoint) return null;
          return (
            <Marker
              key={idx}
              longitude={firstPoint.longitude}
              latitude={firstPoint.latitude}
              anchor='center'
              onClick={() => onSegmentClick(idx)}
            >
              <Box
                component='span'
                className='inline-flex h-8 w-8 cursor-pointer items-center justify-center'
                onMouseEnter={() =>
                  setHoveredInfo({
                    point: firstPoint,
                    startAddress: item.startAddress || firstPoint.address,
                  })
                }
                onMouseLeave={() => setHoveredInfo(null)}
              >
                <MapBackLocationTypeIcon type={item.type} />
              </Box>
            </Marker>
          );
        })}

        {sidebarSelectionMarker &&
          Number.isFinite(sidebarSelectionMarker.latitude) &&
          Number.isFinite(sidebarSelectionMarker.longitude) && (
            <Marker
              longitude={sidebarSelectionMarker.longitude}
              latitude={sidebarSelectionMarker.latitude}
              anchor='bottom'
              offset={[JOURNEY_SIDEBAR_PIN_MARKER.offsetX, JOURNEY_SIDEBAR_PIN_MARKER.offsetY]}
            >
              <Box
                component='span'
                className={styles.sidebarSelectionPinWrap}
                style={
                  {
                    '--sidebar-pin-width': `${JOURNEY_SIDEBAR_PIN_MARKER.width}px`,
                    '--sidebar-pin-height': `${JOURNEY_SIDEBAR_PIN_MARKER.height}px`,
                  } as React.CSSProperties
                }
              >
                <Box component='img' src={iconMapPlayback} alt='' className={styles.sidebarSelectionPin} />
              </Box>
            </Marker>
          )}

        {!playbackPoint && !isPlaying && fallbackLocation && (
          <Marker longitude={fallbackLocation.longitude} latitude={fallbackLocation.latitude} anchor='center'>
            <Box className='relative z-10 flex h-7 w-7 items-center justify-center rounded-full border border-[#1F7BC1] bg-[#2E89C8] shadow-[0_2px_8px_rgba(0,0,0,0.18)]'>
              <MapUserIcon className='h-3.5 w-3.5 [filter:brightness(0)_invert(1)]' />
            </Box>
          </Marker>
        )}
      </Map>

      {!isLoading && safeAllPoints.length === 0 && (
        <Box className='pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 rounded-md bg-white/92 px-3 py-1 text-xs text-[#404854] shadow'>
          {t('empty.noJourneyData')}
        </Box>
      )}
    </Box>
  );
};

export default React.memo(JourneyMap);
